import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Button, Paper } from "@mui/material";
import apiClient from "./utils/apiClient";
import { useAuthStore } from "./store/authStore";
import { useOptInStore } from "./store/optInStore";

const OAuthCallback = () => {
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const loginWithSSO = useAuthStore((state) => state.loginWithSSO);
  const { fetchLicenseOptIn } = useOptInStore();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const providerError = params.get("error");

    if (providerError) {
      setError(`SSO provider returned an error: ${providerError}`);
      return;
    }

    if (!code) {
      setError("Missing authorization code in callback URL.");
      return;
    }

    const query = new URLSearchParams({ code, ...(state && { state }) }).toString();

    apiClient
      .get(`/oauth/callback?${query}`)
      .then(async (res) => {
        if (res?.error) {
          setError(res.detail || "SSO login failed.");
          return;
        }

        if (!res?.access_token) {
          setError("SSO login did not return an access token.");
          return;
        }

        loginWithSSO(res.access_token, res.user || {});

        const userEmail = res.user?.email;
        try {
          const hasAccepted = userEmail ? await fetchLicenseOptIn(userEmail) : false;
          navigate(hasAccepted ? `/report-list/${res.user?.id || userEmail}` : "/license");
        } catch {
          navigate("/license");
        }
      })
      .catch((err) => {
        console.error("SSO callback error:", err);
        setError("SSO login failed. Please try again.");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#b3e5fc",
      }}
    >
      <Paper sx={{ p: 4, borderRadius: 4, textAlign: "center", minWidth: 300 }}>
        {error ? (
          <>
            <Typography variant="h6" color="error" gutterBottom>
              SSO Login Failed
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Back to Login
            </Button>
          </>
        ) : (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">Completing sign-in…</Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthCallback;
